// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import {
  Box,
  Button,
  Divider,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { BiSolidUserPlus } from 'react-icons/bi';

import { type Drivers, Order_By } from '@gql/base-types';
import { useGetOrgDriversQuery } from '../../drivers/queries/getOrgDrivers.query.generated';
import Spacer from '@components/Utils/Spacer';
import DriversTable from '@features/drivers/components/DriversTable';
import TeamControls, { type TeamControlFilters } from './TeamControls';
import DriverMetrics, { type DriverAccessMetric } from './DriverMetrics';
import { buildScopedDriversWhere } from '../utils/whereClauseBuilders';
import { isPortalRole } from '../utils/roleUtils';
import { useGetCurrentUserCustomerIdsQuery } from '../queries/getCurrentUserCustomerIds.query.generated';
import { useUser } from '@store';
import { canHaveDrivers } from '@utils/tiers';

// TYPES ---------------------------------------------------------------- //

interface DriverManagementProps {
  customerIds: number[];
  /** Extra action buttons rendered next to the Add Driver button */
  actions?: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

const DriverManagement: React.FC<DriverManagementProps> = ({ customerIds, actions }) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const navigate = useNavigate();

  // Gate Add Driver on whether the admin has ANY driver-capable rooftop (lite or
  // Mobile Service — see canHaveDrivers), not merely the active one: a multi-rooftop
  // admin whose active rooftop can't have drivers can still add a driver to a capable
  // one. Without this gate a standard-tier admin (no driver-capable rooftop anywhere)
  // can start the flow, and the non-atomic submit — the users row commits, then the
  // driver grant fails with no rollback — leaves an orphaned users row (and consumes
  // the email, blocking a retry). See drivers.spec.ts "lite-gate orphan" for the pin.
  const userId = useUser()?.profile?.id;
  const { data: userCustomerData } = useGetCurrentUserCustomerIdsQuery({
    variables: { userId: userId || 0 },
    skip: !userId,
  });
  const hasDriverCapableRooftop = React.useMemo(
    () => (userCustomerData?.usertocustomers ?? []).some(utc => utc.customer && canHaveDrivers(utc.customer)),
    [userCustomerData],
  );

  // Filter state
  const [filters, setFilters] = React.useState<TeamControlFilters>({
    features: [],
    status: 'active',
    search: '',
  });
  const [accessMetric, setAccessMetric] = React.useState<DriverAccessMetric>('all');

  const where = React.useMemo(
    () => buildScopedDriversWhere(customerIds, { search: filters.search || undefined }),
    [customerIds, filters.search],
  );

  const { data, loading, refetch } = useGetOrgDriversQuery({
    variables: {
      where,
      orderBy: [{ user: { display_name: Order_By.Asc } }],
      limit: 500,
      offset: 0,
    },
    skip: customerIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const allDrivers: Drivers[] = (data?.drivers as Drivers[]) ?? [];

  // Apply client-side filters: status + access metric
  const filteredDrivers = React.useMemo(() => {
    let drivers = allDrivers;

    if (filters.status === 'active') {
      drivers = drivers.filter(d => d.status !== 'terminated' && d.user?.active !== false);
    } else if (filters.status === 'inactive') {
      drivers = drivers.filter(d => d.status === 'terminated' || d.user?.active === false);
    }

    if (accessMetric === 'driver-only') {
      drivers = drivers.filter(d => !isPortalRole(d.user?.default_role));
    } else if (accessMetric === 'portal') {
      drivers = drivers.filter(d => isPortalRole(d.user?.default_role));
    }

    return drivers;
  }, [allDrivers, filters.status, accessMetric]);

  // Compute metric counts (from status-filtered list, before access filter)
  const metricCounts = React.useMemo(() => {
    let drivers = allDrivers;
    if (filters.status === 'active') {
      drivers = drivers.filter(d => d.status !== 'terminated' && d.user?.active !== false);
    } else if (filters.status === 'inactive') {
      drivers = drivers.filter(d => d.status === 'terminated' || d.user?.active === false);
    }
    return {
      all: drivers.length,
      driverOnly: drivers.filter(d => !isPortalRole(d.user?.default_role)).length,
      portal: drivers.filter(d => isPortalRole(d.user?.default_role)).length,
    };
  }, [allDrivers, filters.status]);

  return (
    <Paper className={cls.paper}>
      {/* Header area */}
      <div className={cls.header}>
        {/* Toolbar row */}
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
          <Typography variant="h6" fontWeight={600}>
            Drivers
          </Typography>
          <Box display="flex" gap={1}>
            {actions}
            {hasDriverCapableRooftop && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<BiSolidUserPlus />}
                onClick={() => navigate('/users/add?type=driver')}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Add Driver
              </Button>
            )}
          </Box>
        </Box>

        {/* Controls row */}
        <Box px={2} pb={1}>
          <TeamControls filters={filters} onFiltersChange={setFilters} hideFeatures />
        </Box>

        <Spacer size={1} />

        {/* Metrics row */}
        <Box px={2} pb={2}>
          <DriverMetrics
            allCount={metricCounts.all}
            driverOnlyCount={metricCounts.driverOnly}
            portalCount={metricCounts.portal}
            metric={accessMetric}
            onMetricChange={setAccessMetric}
            loading={loading && allDrivers.length === 0}
          />
        </Box>
      </div>

      <Divider />

      {/* Table */}
      <DriversTable drivers={filteredDrivers} loading={loading} refetch={refetch} />
    </Paper>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  paper: css`
    border-radius: 12px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  `,
  header: css`
    background-color: ${theme.palette.background.paper};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default DriverManagement;
